import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'redux/hooks';
import {
  setPurposeId,
  setIsLoan,
  setMode,
  setCountry,
  setAmount,
  setFXCharges,
  setStudentName,
  setCustomer_rate,
  setAddOnRates,
  setRemitterName,
  setRemitterPAN,
  setRemitterDOB,
  setPurposeIdCopy,
  setCurrency,
} from '../../../redux/features/CreateDeal';
import { useDealData } from './useDealData';
import { setBranch } from 'redux/features/CreateStaff';

export interface IFormInputMap {
  customSettlementRate: string;
  purpose: string;
  country: string;
  amount: number;
  charges: string;
  rate?: number;
  name?: string;
  servicePartner?: string;
  remitter_name?: string;
  pan?: string;
  dob?: string;
  mode?: string;
  isLoan?: boolean;
  branch?: string;
}

interface FormState {
  purpose: {
    id: number;
    label: string;
    requiresLoanSelection: boolean;
    requiresServiceProvider: boolean;
  } | null;
  hasLoan: boolean | null;
  serviceProvider: string | null;
}

export const useDealForm = (updateCurrencyForCountry: (country: string) => string, rate: number) => {
  const form = useForm<IFormInputMap>();
  const dispatch = useDispatch();
  const [activeField, setActiveField] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formData, setFormData] = useState<any[]>([]);
  const { allPurpose } = useDealData();

  const { purpose_id, isLoan, mode } = useAppSelector((state) => state.CreateDeal);

  const purposeMap = {
    purposes: [
      {
        id: 1,
        label: "Education",
        requiresLoanSelection: true,
        requiresServiceProvider: true,
      },
      {
        id: 2,
        label: "GIC",
        requiresLoanSelection: true,
        requiresServiceProvider: false,
      },
      {
        id: 3,
        label: "Gift",
        requiresLoanSelection: false,
        requiresServiceProvider: false,
      },
      {
        id: 4,
        label: "Study Maintenance",
        requiresLoanSelection: false,
        requiresServiceProvider: false,
      },
    ],
  };

  const [formState, setFormState] = useState<FormState>({
    purpose: null,
    hasLoan: null,
    serviceProvider: null,
  });

  const purposeSetter = (id: string, isLoan: string, mode: string) => {
    if (id === "1") {
      const loanBool = isLoan === "true";
      const modeLower = mode?.toLowerCase();
      const purposeId = allPurpose?.find((p) => {
        const isEducation = p?.name?.split(' ')[0]?.toLowerCase() === "education";
        const isLoanMatch = p?.loan === loanBool;
        const isModeMatch = p?.mode?.toLowerCase() === modeLower;
        return isEducation && isLoanMatch && isModeMatch;
      });

      if (purposeId) {
        dispatch(setPurposeIdCopy(purposeId?.id));
      }
    } else if (id === "2") {
      if (isLoan === "true") {
        dispatch(setPurposeIdCopy("14"));
      } else if (isLoan === "false") {
        dispatch(setPurposeIdCopy("2"));
      }
    } else {
      dispatch(setPurposeIdCopy(id));
    }
  };

  const getBelongingBaseId = (purposeId: string) => {
    // Convert purposeId to number for consistent comparison with object IDs
    const pIdNum = Number(purposeId);

    // Define the mappings for each base ID
    const mappings = {
      "1": [1, 8, 9, 10, 11, 15, 16, 17, 18, 19, 20], // Education
      "2": [2, 13, 14], // GIC
      "3": [3], // Study Maintenance
      "4": [4], // Gift/Maintenance
    };

    for (const baseId in mappings) {
      if (mappings[baseId as keyof typeof mappings].includes(pIdNum)) {
        return baseId; // Return the base ID as a string
      }
    }

    // If the purposeId doesn't belong to any of the defined base IDs,
    // return the purposeId itself, as per your original logic's 'else' case.
    return String(purposeId);
  };

  const handlePurposeChange = (purposeId: string) => {
    const selectedPurpose = purposeMap.purposes.find(
      (p) => p.id.toString() === purposeId
    );

    setFormState({
      purpose: selectedPurpose || null,
      hasLoan: null,
      serviceProvider: null,
    });

    // Reset dependent form fields
    form.setValue('isLoan', '');
    form.setValue('mode', '');
  };

  const shouldDisplayField = (fieldName: string) => {
    const { purpose } = formState;

    // if (purpose_id === "1" && mode === "ViaTransfer" && fieldName === "isLoan") {
    //   return purpose?.requiresLoanSelection === false;
    // }

    if (fieldName === "isLoan") {
      return purpose?.requiresLoanSelection === true;
    }

    if (fieldName === "mode") {
      return purpose?.requiresServiceProvider === true;
    }

    return true;
  };

  const getFormConfig = (currency: string, purpose: any[], branches: any[]) => {
    const baseConfig = [
      [

        {
          label: "What is your purpose for sending the money ?",
          name: "purpose",
          type: "select",
          placeholder: "Select a purpose for sending money",
          options: purpose,
        },
        {
          label: "Mode",
          name: "mode",
          type: "select",
          optional: true,
          alwaysShow: true,
          options: [
            { value: "Flywire", label: "Flywire" },
            { value: "WesternUnion", label: "Western Union" },
            { value: 'CIBC', label: 'CIBC' },
            { value: 'ViaTransfer', label: 'Via Transfer' },
            { value: 'PayMyTuition', label: 'Pay My Tuition' }
          ],
        },
        {
          label: "Have you taken loan?",
          name: "isLoan",
          type: "radio",
          optional: true,
          alwaysShow: true,
          options: [
            { value: "true", label: "YES" },
            { value: "false", label: "NO" },
          ],
        },
        {
          label: "Where do you want to send the money ?",
          name: "country",
          type: "select",
          placeholder: "Select country",
          options: [],
        },
        {
          label: "Enter the amount you want to send.",
          name: "amount",
          type: "currency",
          currency,
        },
        {
          label: "Select the Foreign Charges",
          name: "charges",
          type: "radio",
          optional: true,
          alwaysShow: true,
          options: [
            { value: "BEN", label: "BEN" },
            { value: "OUR", label: "OUR" },
          ],
        },
        {
          label: "Enter the customer rate (optional)",
          name: "rate",
          type: "currency",
          currency: "INR",
          optional: true,
        },
      ],
      [
        {
          label: "Custom Settlement Rate",
          name: "customSettlementRate",
          type: "settlement-rate-input",
          currency: "INR",
          optional: true,
          alwaysShow: true,
        },
        {
          label: "What is the name of student? (optional)",
          name: "name",
          type: "text",
          optional: true,
          placeholder: "John Doe",
          alwaysShow: true,
        },
      ],
    ];

    return baseConfig;
  };

  useEffect(() => {
    purposeSetter(purpose_id, isLoan, mode);
  }, [purpose_id, isLoan, mode]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name !== undefined && value[name] !== undefined) {
        switch (name) {
          case "purpose":
            dispatch(setPurposeId(value[name]));
            handlePurposeChange(value[name]);
            break;
          case "isLoan":
            dispatch(setIsLoan(value[name]));
            break;
          case "mode":
            dispatch(setMode(value[name]));
            break;
          case "country":
            dispatch(setCountry(value[name]));
            const newCurrency = updateCurrencyForCountry(value[name]);
            dispatch(setCurrency(newCurrency));
            break;
          case "amount":
            if (value["amount"]) {
              form.setValue("charges", "BEN");
              setActiveField((prev) => prev + 1);
            }
            dispatch(setAmount(String(value[name])));
            break;
          case "charges":
            dispatch(setFXCharges(value[name]));
            break;
          case "name":
            dispatch(setStudentName(value[name]));
            break;
          case "rate":
            dispatch(setCustomer_rate(value[name]?.toString() || ""));
            dispatch(
              setAddOnRates(
                parseFloat(String(value[name])) - parseFloat(rate.toFixed(4))
              )
            );
            break;
          case "remitter_name":
            dispatch(setRemitterName(value[name]));
            break;
          case "pan":
            dispatch(setRemitterPAN(value[name]));
            break;
          case "dob":
            dispatch(setRemitterDOB(value[name]));
            break;
        }

        // Update activeField if necessary
        const currentFieldIndex = formData.findIndex(
          (field) => field.name === name
        );
        if (currentFieldIndex === activeField && value[name]) {
          setActiveField((prev) => Math.min(prev + 1, formData.length - 1));
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form.watch, rate, updateCurrencyForCountry, activeField, formData]);

  return {
    form,
    activeField,
    setActiveField,
    currentStep,
    setCurrentStep,
    formData,
    setFormData,
    shouldDisplayField,
    getFormConfig,
    handlePurposeChange,
    purposeSetter,
    getBelongingBaseId
  };
};